import React, { useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { simulateBulkUpdate, simulateBulkDelete, buildBulkUpdates } from "../services/dryRunService";
import { createBackups } from "../services/backupService";
import { bulkDeleteDocuments, bulkUpdateDocuments, fetchDocumentsByIds } from "../services/firestoreDataService";
import { writeAuditLog } from "../services/auditLogService";
import { trackWrite, trackDelete } from "../services/monitorService";
import DryRunModal from "./DryRunModal";

const OPS = [
  { type: "setField", label: "Set field" },
  { type: "deleteField", label: "Remove field" },
  { type: "renameField", label: "Rename field" },
  { type: "addField", label: "Add field" },
  { type: "replaceText", label: "Replace text" },
  { type: "uppercase", label: "Uppercase" },
  { type: "lowercase", label: "Lowercase" },
  { type: "trim", label: "Trim" },
  { type: "numericDelta", label: "Numeric delta" },
];

export default function BulkOperationsPanel({ onDone }) {
  const { collectionName, rows, selected, setSelected, user, loadDocuments, can, Permission } = useFirestoreAdmin();
  const [opType, setOpType] = useState("setField");
  const [field, setField] = useState("");
  const [value, setValue] = useState("");
  const [newField, setNewField] = useState("");
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [preview, setPreview] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const selectedRows = rows.filter((r) => selected.has(r.id));

  const runDryRun = (action) => {
    if (!selectedRows.length) {
      toast.error("Select documents first");
      return;
    }
    if (action === "delete") {
      setPreview(simulateBulkDelete(selectedRows));
      setPendingAction("delete");
      return;
    }
    const request = {
      type: opType,
      field: field.trim(),
      value,
      newField: newField.trim(),
      find,
      replace,
      numericDelta: Number(value) || 0,
    };
    setPreview(simulateBulkUpdate(selectedRows, request));
    setPendingAction({ type: "update", request });
  };

  const execute = async () => {
    if (!can(Permission.bulk) && !can(Permission.delete)) {
      toast.error("Insufficient permissions");
      return;
    }
    setExecuting(true);
    try {
      const ids = selectedRows.map((r) => r.id);
      const oldDocs = await fetchDocumentsByIds(collectionName, ids);
      await createBackups({
        collectionName,
        documents: oldDocs,
        adminUid: user?.uid,
        adminName: user?.email,
        operation: pendingAction === "delete" ? "bulk_delete" : "bulk_update",
      });

      if (pendingAction === "delete") {
        await bulkDeleteDocuments(collectionName, ids, user?.uid || user?.email);
        trackDelete(ids.length);
        await writeAuditLog({
          adminName: user?.email,
          adminUid: user?.uid,
          action: "bulk_delete",
          collection: collectionName,
          documentIds: ids,
          oldValues: oldDocs,
        });
        toast.success(`Deleted ${ids.length} documents`);
      } else {
        const updates = buildBulkUpdates(selectedRows, pendingAction.request);
        await bulkUpdateDocuments(collectionName, updates, user?.uid || user?.email);
        trackWrite(updates.length);
        await writeAuditLog({
          adminName: user?.email,
          adminUid: user?.uid,
          action: "bulk_update",
          collection: collectionName,
          documentIds: ids,
          oldValues: oldDocs,
          newValues: pendingAction.request,
        });
        toast.success(`Updated ${updates.length} documents`);
      }
      setPreview(null);
      setSelected(new Set());
      await loadDocuments();
      onDone?.();
    } catch (e) {
      toast.error(e?.message || "Bulk operation failed");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="fa-card p-4">
      <h3 className="mb-3 font-semibold">Bulk operations</h3>
      <div className="flex flex-wrap gap-2">
        <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" value={opType} onChange={(e) => setOpType(e.target.value)}>
          {OPS.map((o) => (
            <option key={o.type} value={o.type}>{o.label}</option>
          ))}
        </select>
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="Field" value={field} onChange={(e) => setField(e.target.value)} />
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="Value" value={value} onChange={(e) => setValue(e.target.value)} />
        {opType === "renameField" && (
          <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="New field" value={newField} onChange={(e) => setNewField(e.target.value)} />
        )}
        {opType === "replaceText" && (
          <>
            <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="Find" value={find} onChange={(e) => setFind(e.target.value)} />
            <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="Replace" value={replace} onChange={(e) => setReplace(e.target.value)} />
          </>
        )}
        <button type="button" className="fa-btn fa-btn-primary" onClick={() => runDryRun("update")} disabled={!selectedRows.length}>
          Dry run update
        </button>
        <button type="button" className="fa-btn fa-btn-danger" onClick={() => runDryRun("delete")} disabled={!selectedRows.length}>
          Dry run delete
        </button>
      </div>
      {preview && (
        <DryRunModal
          preview={preview}
          executing={executing}
          onCancel={() => setPreview(null)}
          onExecute={execute}
        />
      )}
    </div>
  );
}
